import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Activity } from 'lucide-react';
import { DashCard, DashTooltip, DASH_COLORS, compactMoney, money } from './DashCard';

const SERIES = [
  { key: 'gross', name: 'Gross', color: DASH_COLORS.gross },
  { key: 'net', name: 'Net to carriers', color: DASH_COLORS.net },
  { key: 'fees', name: 'Dispatch fees', color: DASH_COLORS.fees },
];

const shortDate = (v) => {
  const [, m, d] = (v || '').split('-');
  return m ? `${Number(m)}/${Number(d)}` : v;
};

const SideMetric = ({ label, value, pct, color, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-baseline justify-between">
      <span className="text-sm text-slate-600 dark:text-slate-300">{label}</span>
      <span className="font-mono text-sm font-semibold text-slate-800 dark:text-slate-100">{value}</span>
    </div>
    {pct !== undefined && (
      <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
        <div className="h-full rounded-full dash-bar" style={{ width: `${Math.min(Math.max(pct, 2), 100)}%`, background: color }} />
      </div>
    )}
  </div>
);

export const RevenueTrendCard = ({ series = [], last30 = {} }) => {
  const netShare = last30.gross ? (last30.net / last30.gross) * 100 : 0;
  return (
    <DashCard
      title="Revenue Trend"
      subtitle="Last 12 weeks · gross, net to carriers and dispatch fees"
      icon={Activity}
      delay={120}
      testid="dashboard-revenue-trend"
      action={
        <div className="hidden sm:flex items-center gap-4 text-sm text-slate-600 dark:text-slate-300">
          {SERIES.map((s) => (
            <span key={s.key} className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ background: s.color }} /> {s.name}
            </span>
          ))}
        </div>
      }
    >
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        <div className="xl:col-span-3 h-[300px]" data-testid="revenue-area-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 10, right: 8, left: 4, bottom: 0 }}>
              <defs>
                {SERIES.map((s) => (
                  <linearGradient key={s.key} id={`dash-grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={s.color} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={s.color} stopOpacity={0.03} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="4 6" stroke="currentColor" className="text-slate-200 dark:text-slate-800" />
              <XAxis dataKey="week_start" tickFormatter={shortDate} tick={{ fontSize: 13, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={compactMoney} tick={{ fontSize: 13, fill: '#64748b' }} axisLine={false} tickLine={false} width={64} />
              <Tooltip content={<DashTooltip labelFormatter={(v) => `Week of ${shortDate(v)}`} />} />
              {SERIES.map((s) => (
                <Area
                  key={s.key}
                  type="natural"
                  dataKey={s.key}
                  name={s.name}
                  stroke={s.color}
                  strokeWidth={2.25}
                  fill={`url(#dash-grad-${s.key})`}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                  animationDuration={900}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="flex flex-col justify-center gap-5 xl:border-l xl:pl-6 border-slate-100 dark:border-slate-800">
          <p className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-300">Last 30 days</p>
          <SideMetric label="Gross revenue" value={money(last30.gross)} color={DASH_COLORS.gross} testid="metric-gross-30" />
          <SideMetric label="Net share to carriers" value={`${netShare.toFixed(1)}%`} pct={netShare} color={DASH_COLORS.net} testid="metric-net-share" />
          <SideMetric label="Dispatch fee rate" value={`${last30.fee_pct || 0}%`} pct={last30.fee_pct || 0} color={DASH_COLORS.navy} testid="metric-fee-pct" />
          <SideMetric label="Avg gross / load" value={money(last30.avg_per_load)} color={DASH_COLORS.slate} testid="metric-avg-load" />
          <div className="pt-1">
            <div className="flex items-center justify-between rounded-xl bg-slate-50 dark:bg-slate-800/60 px-3 py-2" data-testid="metric-loads-per-unit">
              <div className="text-sm uppercase text-slate-500 dark:text-slate-300">Loads / unit</div>
              <div className="font-mono text-sm font-semibold">{last30.loads_per_unit ?? 0}</div>
            </div>
          </div>
        </div>
      </div>
    </DashCard>
  );
};
